'use client';

import { type PropsWithChildren, type ReactNode, Suspense, useEffect, useMemo } from 'react';

type CacheStore = {
  get: () => ReactNode | undefined;
  set: (value: ReactNode) => void;
};

function createCacheStore(): CacheStore {
  let cached: ReactNode | undefined = undefined;
  return {
    get: () => cached,
    set: (value: ReactNode): void => {
      cached = value;
    },
  };
}

const suspenseCacheMap: Map<string, CacheStore> = new Map();

type ReactTrackedPromise = Promise<unknown> & {
  status?: 'pending' | 'fulfilled' | 'rejected';
};

function isResolvedPromiseOrReactNode(node: ReactNode): boolean {
  if (typeof node === 'object' && node instanceof Promise) {
    const promise = node as ReactTrackedPromise;
    return promise.status === 'fulfilled' || promise.status === 'rejected';
  }
  return true;
}

/**
 * Inner wrapper that caches children after a successful render.
 * useEffect ensures the cache is only updated after React commits,
 * so if children suspend, the cache retains the previous (working) children.
 *
 * @param props - component props
 * @param props.children - the children to render and cache
 * @param props.cache - mutable cache store for persisting rendered content
 * @returns the children as-is
 */
function SuspenseCache({ children, cache }: { children: ReactNode; cache: CacheStore }): ReactNode {
  useEffect(() => {
    if (isResolvedPromiseOrReactNode(children)) {
      cache.set(children);
    }
  }, [children, cache]);

  return children;
}

type CachedSuspenseProps = PropsWithChildren<{
  fallback?: ReactNode;
  cacheKey: string;
}>;

/**
 * A Suspense wrapper that shows previously rendered content as fallback
 * instead of a loading skeleton on subsequent renders.
 *
 * - First render: shows the provided `fallback` while children resolve.
 * - After resolve: caches the children.
 * - Next suspension: shows the cached (stale) children as fallback,
 *   giving a seamless "stale-while-revalidate" experience.
 *
 * @param props - component props
 * @param props.fallback - fallback to show on first render before children resolve
 * @param props.children - async children to render inside Suspense
 * @param props.cacheKey - unique key to identify cache for this Suspense instance
 * @returns Suspense-wrapped children with cached fallback behaviour
 * @example
 * ```tsx
 * <CachedSuspense cacheKey="MyUniqueKey" fallback={<Skeleton />}>
 *   <TheAsyncComponent />
 * </CachedSuspense>
 * ```
 */
export default function CachedSuspense({ fallback, children, cacheKey }: CachedSuspenseProps): ReactNode {
  const cache = useMemo(() => {
    let cache = suspenseCacheMap.get(cacheKey);
    if (!cache) {
      cache = createCacheStore();
      if (typeof window !== 'undefined') {
        suspenseCacheMap.set(cacheKey, cache);
      }
    }
    return cache;
  }, [cacheKey]);

  const resolvedFallback = cache.get() === undefined ? (fallback ?? undefined) : cache.get();

  return (
    <Suspense fallback={resolvedFallback}>
      <SuspenseCache cache={cache}>{children}</SuspenseCache>
    </Suspense>
  );
}
